<template>
  <div class="assigned-team-members">
    <div class="card-header">
      <h5 class="card-title">
        <i class="fas fa-users text-indigo-600 mr-2" />
        Assigned Team Members
      </h5>
    </div>

    <div
      v-if="loading"
      class="card-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-indigo-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading team assignments...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else
      class="card-body">
      <!-- View Mode -->
      <div v-if="!isEditing">
        <div
          v-if="hasAssignments"
          class="space-y-2">
          <div
            v-for="(assignment, role) in currentAssignments"
            v-show="assignment && assignment !== null"
            :key="role"
            class="team-member-card">
            <div class="team-member-content">
              <i
                :class="getRoleIcon(role)"
                class="team-member-icon" />
              <div class="flex-1">
                <div class="team-member-role">
                  {{ formatRoleName(role) }}
                </div>
                <div
                  v-if="assignment"
                  class="team-member-name">
                  {{ assignment.fullName }}
                </div>
                <a
                  v-if="assignment && assignment.email"
                  :href="`mailto:${assignment.email}`"
                  class="team-member-email">
                  {{ assignment.email }}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="empty-state">
          <i class="fas fa-user-slash empty-state-icon" />
          <p class="empty-state-text">
            No team members assigned yet
          </p>
        </div>

        <div class="mt-4">
          <button
            class="action-button action-button-primary"
            @click="startEditing">
            <i class="fas fa-user-plus mr-1.5" />
            {{ hasAssignments ? 'Edit Assignments' : 'Assign Team Members' }}
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <form
        v-else
        class="space-y-4"
        @submit.prevent="saveAssignments">
        <div
          v-for="(members, role) in teamMembersByRole"
          :key="role"
          class="form-group">
          <label class="form-label">
            <i
              :class="getRoleIcon(role)"
              class="mr-1.5" />
            {{ formatRoleName(role) }}
          </label>
          <select
            v-model="form[role]"
            class="form-select">
            <option value="">
              -- Select {{ formatRoleName(role) }} --
            </option>
            <option
              v-for="member in members"
              :key="member.id"
              :value="member.id">
              {{ member.displayName }}
            </option>
          </select>
        </div>

        <div
          v-if="error"
          class="error-banner">
          <i class="fas fa-exclamation-circle mr-2" />
          {{ error }}
        </div>

        <div class="form-actions">
          <div class="flex gap-2">
            <button
              type="submit"
              :disabled="saving"
              class="action-button action-button-success">
              <i
                v-if="saving"
                class="fas fa-spinner fa-spin mr-1.5" />
              <i
                v-else
                class="fas fa-save mr-1.5" />
              Save Assignments
            </button>
            <button
              type="button"
              :disabled="saving"
              class="action-button action-button-secondary"
              @click="cancelEditing">
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const isEditing = ref(false)
const error = ref('')
const teamMembersByRole = ref({})
const currentAssignments = ref({})
const form = ref({
  activationSpecialist: '',
  onboardingSpecialist: '',
  performanceManager: '',
  resellerRep: ''
})

// Role configuration
const roleConfig = {
  activationSpecialist: {
    icon: 'fas fa-rocket',
    label: 'Activation Specialist'
  },
  onboardingSpecialist: {
    icon: 'fas fa-user-check',
    label: 'Onboarding Specialist'
  },
  performanceManager: {
    icon: 'fas fa-chart-line',
    label: 'Performance Manager'
  },
  resellerRep: {
    icon: 'fas fa-handshake',
    label: 'Reseller Rep'
  }
}

// Computed
const hasAssignments = computed(() => Object.values(currentAssignments.value).some((assignment) => assignment !== null))

// Methods
const getRoleIcon = (role) => roleConfig[role]?.icon || 'fas fa-user'

const formatRoleName = (role) => roleConfig[role]?.label || role

const fetchTeamAssignments = async() => {
  try {
    loading.value = true
    error.value = ''
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/team_assignments`)

    if (response.data.success) {
      teamMembersByRole.value = response.data.teamMembersByRole || {}
      // The backend returns snake_case but Vue auto-converts to camelCase
      currentAssignments.value = response.data.currentAssignments || {}
    }
  } catch (err) {
    console.error('Error fetching team assignments:', err) // eslint-disable-line no-console
    error.value = 'Failed to load team assignments'
    mdtoast.error('Failed to load team assignments', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const startEditing = () => {
  // Initialize form with current assignments
  form.value = {
    activationSpecialist: currentAssignments.value.activationSpecialist?.id || '',
    onboardingSpecialist: currentAssignments.value.onboardingSpecialist?.id || '',
    performanceManager: currentAssignments.value.performanceManager?.id || '',
    resellerRep: currentAssignments.value.resellerRep?.id || ''
  }
  error.value = ''
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
  error.value = ''
}

const saveAssignments = async() => {
  try {
    saving.value = true
    error.value = ''

    // Convert camelCase to snake_case for the request
    const payload = {
      teamAssignments: {
        activation_specialist: form.value.activationSpecialist,
        onboarding_specialist: form.value.onboardingSpecialist,
        performance_manager: form.value.performanceManager,
        reseller_rep: form.value.resellerRep
      }
    }

    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/team_assignments`,
      payload
    )

    if (response.data.success) {
      // The response comes with camelCase conversion
      const assignments = response.data.assignedTeamMembers || {}

      // Update currentAssignments with the properly formatted data
      Object.keys(assignments).forEach((role) => {
        currentAssignments.value[role] = assignments[role]
      })

      isEditing.value = false
      mdtoast.success('Team assignments updated successfully', { duration: 3000 })
    } else {
      error.value = response.data.error || 'Failed to save assignments'
    }
  } catch (err) {
    console.error('Error saving team assignments:', err) // eslint-disable-line no-console
    error.value = err.response?.data?.error || 'Failed to save team assignments'
    mdtoast.error(error.value, { duration: 3000 })
  } finally {
    saving.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchTeamAssignments()
})
</script>

<style scoped>
.assigned-team-members {
  @apply bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden;
}

.card-header {
  @apply px-5 py-4 bg-gray-50 border-b border-gray-200;
}

.card-title {
  @apply text-base font-semibold text-gray-900 flex items-center;
}

.card-body {
  @apply p-5;
}

.team-member-card {
  @apply bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors duration-200;
}

.team-member-content {
  @apply flex items-start gap-3;
}

.team-member-icon {
  @apply w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0;
}

.team-member-role {
  @apply text-xs font-semibold text-gray-600 uppercase tracking-wider mb-0.5;
}

.team-member-name {
  @apply text-sm font-medium text-gray-900;
}

.team-member-email {
  @apply text-xs text-indigo-600 hover:text-indigo-800 hover:underline transition-colors;
}

.empty-state {
  @apply text-center py-8;
}

.empty-state-icon {
  @apply text-4xl text-gray-300 mb-3;
}

.empty-state-text {
  @apply text-sm text-gray-500;
}

.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-medium text-gray-700 flex items-center;
}

.form-select {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
         transition-colors duration-200;
}

.error-banner {
  @apply text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center;
}

.form-actions {
  @apply mt-6 pt-4 border-t border-gray-200;
}

.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500;
}

.action-button-success {
  @apply bg-green-600 text-white hover:bg-green-700 focus:ring-green-500;
}

.action-button-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}
</style>
